import React, { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router';
import { AppShell } from 'components/AppShell';
import { LoginCallback } from '@okta/okta-react';
import LoadingScreenComp from 'Utils/components/LoadingScreenComp';
import ErrorPage from 'pages/ErrorPage';
import { Unauthorized } from './Unauthorized';
import { useRBAC } from 'hooks/useRBAC';
import { ApplicationTabsEnum } from './Enums';

const Settings = lazy(() => import('pages/Settings'));
const Dashboard = lazy(() => import('pages/Dashboard'));
const Products = lazy(() => import('pages/Products'));
const Customers = lazy(() => import('pages/Customers'));
const Plugins = lazy(() => import('pages/Plugins'));
const Connectors = lazy(() => import('pages/Connectors'));
const Integrations = lazy(() => import('pages/Integrations'));
const Features = lazy(() => import('pages/Features'));
const NotFound = lazy(() => import('pages/NotFound'));
const MetadataGenerator = lazy(() => import('pages/MetadataGenerator'));
const ContentIndex = lazy(() => import('pages/ContentIndex'));
const LanguageTranslator = lazy(() => import('pages/LanguageTranslator'));
const Campaigns = lazy(() => import('pages/Campaigns'));
const Logout = lazy(() => import('./Logout'));

export const Router: React.FC = () => {
  const { lookupPermissions } = useRBAC();
  return (
    <Suspense fallback={<AppShell loading />}>
      <Routes>
        <Route path="/*" element={<AppShell />}>
          <Route
            path="/*"
            element={
              lookupPermissions([ApplicationTabsEnum.DASHBOARD], 'R') ? (
                <Dashboard />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="products/*"
            element={
              lookupPermissions([ApplicationTabsEnum.PRODUCTS], 'R') ? (
                <Products />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="customers/*"
            element={
              lookupPermissions([ApplicationTabsEnum.CUSTOMERS], 'R') ? (
                <Customers />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="plugins/*"
            element={
              lookupPermissions([ApplicationTabsEnum.PLUGINS], 'R') ? (
                <Plugins />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="connectors/*"
            element={
              lookupPermissions([ApplicationTabsEnum.CONNECTORS], 'R') ? (
                <Connectors />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="integrations/*"
            element={
              lookupPermissions([ApplicationTabsEnum.INTEGRATIONS], 'R') ? (
                <Integrations />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="features/*"
            element={
              lookupPermissions([ApplicationTabsEnum.FEATURES], 'R') ? (
                <Features />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="metadataGenerator/*"
            element={
              lookupPermissions([ApplicationTabsEnum.META_GENERATORS], 'R') ? (
                <MetadataGenerator />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="settings/*"
            element={
              lookupPermissions([ApplicationTabsEnum.ACCESS], 'R') ? (
                <Settings />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="reindexContent/*"
            element={
              lookupPermissions([ApplicationTabsEnum.REINDEX_CONTENT], 'R') ? (
                <ContentIndex />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="localizationTable/*"
            element={
              lookupPermissions(
                [ApplicationTabsEnum.LOCALIZATION_TABLE],
                'R'
              ) ? (
                <LanguageTranslator />
              ) : (
                <NotFound />
              )
            }
          />
          <Route
            path="campaigns/*"
            element={
              lookupPermissions([ApplicationTabsEnum.CAMPAIGNS], 'R') ? (
                <Campaigns />
              ) : (
                <NotFound />
              )
            }
          />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route path="error" element={<ErrorPage error="" />} />

        {process.env.NODE_ENV !== 'development' && (
          <Route
            path="/login/callback"
            element={<LoginCallback loadingElement={<LoadingScreenComp />} />}
          />
        )}
        <Route path="/logout" element={<Logout />} />
        <Route path="/403" element={<Unauthorized />} />
      </Routes>
    </Suspense>
  );
};
